import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { Button, InputText, Text } from "@pentabd/ui";

import { useRoomConferenceById } from "@hooks/inventory/roomConference/useRoomConferenceDetail";
import { useUser } from "@hooks/useUser";

import {
  roomConferenceValidationSchema,
  ROOM_CONFERENCE,
} from "@validations/Inventory/roomConference/roomConferenceValidation";

import { ErrorModal, FileRow, InputTextRow, SuccessModal } from "@components";
import {
  RoomConferenceDetailResponseType,
  RoomConferenceType,
} from "@type/inventory/room-conference";

import { getAccess } from "@helpers/permissionController";

import { ROUTES } from "@constants/routes";
import { revertSingleDocumentData } from "@utils/documentDataConvert";

const { INVENTORY } = ROUTES;

const { CONFERENCE_ROOM } = INVENTORY;

export default function RoomConferenceDetailsEditForm() {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { roomConferenceState, updateRoomConferenceHandler } =
    useRoomConferenceById();

  const { user } = useUser();
  const { realm_roles: roles } = user || {};

  const { getSingleData, updateError, updateSuccess } =
    roomConferenceState || ({} as RoomConferenceDetailResponseType);

  const { data } = getSingleData || ({} as RoomConferenceType);

  console.log("response  data", data);

  const method = useForm<RoomConferenceType>({
    resolver: yupResolver(roomConferenceValidationSchema),
    values: revertSingleDocumentData(data) as RoomConferenceType,
  });

  const { register, control, setValue } = method;

  const closeErrorModal = () => setIsErrorModalOpen(false);
  const successAction = () => navigate(CONFERENCE_ROOM);

  const submitHandler = (status: string) => {
    const updateReqBody = {
      status: status,
    };

    updateRoomConferenceHandler(updateReqBody);
  };

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
    };
    return reset();
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      setIsSuccessModalOpen(true);
    }
    if (updateError) {
      setIsErrorModalOpen(true);
    }
  }, [updateSuccess, updateError]);

  console.log("data", data);

  return (
    <div>
      <>
        <SuccessModal isModalOpen={isSuccessModalOpen} action={successAction} />
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
      </>
      <FormProvider {...method}>
        <div className="container d-flex flex-column gap-6 border-bottom mt-10">
          <InputTextRow
            label="INVENTORY_ROOM_CONFERENCE.FORM.OFFICE"
            register={{ ...register(ROOM_CONFERENCE.OFFICE) }}
            disabled
          />

          <InputTextRow
            label="INVENTORY_ROOM_CONFERENCE.FORM.BUILDING"
            register={{ ...register(ROOM_CONFERENCE.BUILDING) }}
            disabled
          />

          <InputTextRow
            label="INVENTORY_ROOM_CONFERENCE.FORM.CONFERENCE_ROOM"
            register={{ ...register(ROOM_CONFERENCE.ROOM) }}
            disabled
          />

          <InputTextRow
            label="INVENTORY_ROOM_CONFERENCE.FORM.MEETING_PURPOSE"
            register={{ ...register(ROOM_CONFERENCE.MEETING_PURPOSE) }}
            disabled
          />

          <InputTextRow
            label="INVENTORY_ROOM_CONFERENCE.FORM.MEETING_START_TIME"
            register={{ ...register(ROOM_CONFERENCE.BOOKING_START_TIME) }}
            disabled
          />

          <InputTextRow
            label="INVENTORY_ROOM_CONFERENCE.FORM.MEETING_START_TIME"
            register={{ ...register(ROOM_CONFERENCE.BOOKING_START_TIME) }}
            disabled
          />

          <InputTextRow
            label="INVENTORY_ROOM_CONFERENCE.FORM.MEETING_END_TIME"
            register={{ ...register(ROOM_CONFERENCE.BOOKING_END_TIME) }}
            disabled
          />

          <InputTextRow
            label="INVENTORY_ROOM_CONFERENCE.FORM.TOTAL_ATTENDEES"
            register={{ ...register(ROOM_CONFERENCE.TOTAL_ATTENDEES) }}
            disabled
          />

          <InputTextRow
            label="INVENTORY_ROOM_CONFERENCE.FORM.REFRESHMENT_SUPPORT"
            register={{ ...register(ROOM_CONFERENCE.REFRESHMENT_DETAILS) }}
            disabled
          />

          <FileRow
            name={ROOM_CONFERENCE.DOCUMENT}
            control={control}
            setValue={setValue}
            register={register}
            disabled
            fileDetails={["documentName", "date", "remarks", "memorialNo"]}
          />

          {getAccess(roles, "ROOM_BOOKING") &&
          data &&
          data.status !== "APPROVED" &&
          data.status !== "REJECTED" ? (
            <>
              {" "}
              <div className="row">
                <div className="col-lg-3"></div>
                <div className="col-lg-7">
                  <div className="d-flex justify-content-end gap-5">
                    <Button
                      fill="outline"
                      size="sm"
                      type="danger"
                      htmlType="button"
                      onClick={() => submitHandler("REJECTED")}
                    >
                      {t(
                        "INVENTORY_ROOM_CONFERENCE.DETAILS_FORM.REJECT_BUTTON_TEXT",
                      )}
                    </Button>
                    <Button
                      fill="fill"
                      size="sm"
                      type="info"
                      htmlType="button"
                      onClick={() => {
                        submitHandler("APPROVED");
                      }}
                    >
                      {t(
                        "INVENTORY_ROOM_CONFERENCE.DETAILS_FORM.APPROVAL_BUTTON_TEXT",
                      )}
                    </Button>
                  </div>
                </div>
              </div>{" "}
            </>
          ) : (
            <></>
          )}
        </div>
      </FormProvider>
    </div>
  );
}
